import React, { useState, useRef, useEffect } from "react";
import { MousePointer2, PenTool, Trash2, Check, Square } from "lucide-react";

export default function FloorplanEditor({ zones, selectedZoneId, onSelectZone, onAddZone, onUpdateZone, onDeleteZone }) {
  const [mode, setMode] = useState("select"); // 'select' | 'draw' | 'draw-outline'
  const [drawingPoints, setDrawingPoints] = useState([]);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  
  const svgRef = useRef(null);
  const GRID_SIZE = 20;

  // Handles snapping mouse coordinates to the grid
  const getGridCoords = (e) => {
    if (!svgRef.current) return { x: 0, y: 0 };
    const rect = svgRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    return {
      x: Math.round(x / GRID_SIZE) * GRID_SIZE,
      y: Math.round(y / GRID_SIZE) * GRID_SIZE
    };
  };

  const handleMouseMove = (e) => {
    const coords = getGridCoords(e);
    setMousePos(coords);
  };

  const handleSvgClick = (e) => {
    if (mode === "draw" || mode === "draw-outline") {
      const coords = getGridCoords(e);
      // Check if clicking near the first point to close the polygon
      if (drawingPoints.length > 2) {
        const firstPoint = drawingPoints[0];
        const dist = Math.hypot(coords.x - firstPoint.x, coords.y - firstPoint.y);
        if (dist < GRID_SIZE) {
          finishDrawing();
          return;
        }
      }
      setDrawingPoints([...drawingPoints, coords]);
    } else {
      // If clicking empty space in select mode, deselect
      if (e.target.tagName === "svg" || e.target.tagName === "rect") {
        onSelectZone(null);
      }
    }
  };

  const finishDrawing = () => {
    if (drawingPoints.length > 2) {
      onAddZone(drawingPoints, mode === "draw-outline");
    }
    setDrawingPoints([]);
    setMode("select");
  };

  const cancelDrawing = () => {
    setDrawingPoints([]);
    setMode("select");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      cancelDrawing();
    } else if (e.key === "Enter" && (mode === "draw" || mode === "draw-outline")) {
      finishDrawing();
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [drawingPoints, mode]);

  const activePointsStr = drawingPoints.map(p => `${p.x},${p.y}`).join(" ");
  // Add current mouse position to the active polygon preview
  const previewPointsStr = drawingPoints.length > 0 ? `${activePointsStr} ${mousePos.x},${mousePos.y}` : "";

  return (
    <div className="floorplan-container">
      <div className="floorplan-toolbar">
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button 
            className={`btn ${mode === "select" ? "btn-primary" : "btn-secondary"}`}
            onClick={() => setMode("select")}
            title="Select tool"
          >
            <MousePointer2 size={16} /> Select
          </button>
          <button 
            className={`btn ${mode === "draw" ? "btn-primary" : "btn-secondary"}`}
            onClick={() => { setMode("draw"); onSelectZone(null); }}
            title="Draw Zone Polygon"
          >
            <PenTool size={16} /> Draw Zone
          </button>
          <button 
            className={`btn ${mode === "draw-outline" ? "btn-primary" : "btn-secondary"}`}
            onClick={() => { setMode("draw-outline"); onSelectZone(null); }}
            title="Draw Layout Outline (Not a Zone)"
          >
            <Square size={16} /> Draw Outline
          </button>
        </div>
        
        {(mode === "draw" || mode === "draw-outline") && (
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <span style={{ fontSize: "0.85rem", color: "var(--text-secondary)" }}>
              Click on the grid to draw. Click the starting point or press Enter to finish.
            </span>
            <button className="btn btn-secondary btn-xs-text" onClick={cancelDrawing} style={{ padding: "0.25rem 0.5rem" }}>Cancel</button>
            <button className="btn btn-primary btn-xs-text" onClick={finishDrawing} style={{ padding: "0.25rem 0.5rem" }}>
              <Check size={14} /> Finish
            </button>
          </div>
        )}
      </div>

      <div className="floorplan-svg-wrapper">
        <svg
          ref={svgRef}
          width="100%"
          height="100%"
          style={{ minHeight: "500px" }}
          viewBox="0 0 1200 800"
          preserveAspectRatio="xMidYMid meet" 
          className={`floorplan-svg ${mode === "draw" ? "drawing-mode" : ""}`}
          onMouseMove={handleMouseMove}
          onClick={handleSvgClick}
        >
          {/* Grid Pattern definition */}
          <defs>
            <pattern id="grid" width={GRID_SIZE} height={GRID_SIZE} patternUnits="userSpaceOnUse">
              <rect width={GRID_SIZE} height={GRID_SIZE} fill="none" />
              <circle cx={GRID_SIZE} cy={GRID_SIZE} r="1.5" fill="rgba(255, 255, 255, 0.15)" />
            </pattern>
          </defs>

          {/* Background */}
          <rect width="100%" height="100%" fill="url(#grid)" />

          {/* Render Existing Zones */}
          {zones.map((zone) => {
            if (!zone.points || zone.points.length === 0) return null;
            const ptsStr = zone.points.map(p => `${p.x},${p.y}`).join(" ");
            const isSelected = selectedZoneId === zone.id;
            
            if (zone.isOutline) {
              return (
                <g key={zone.id} className={`zone-group ${isSelected ? "selected" : ""}`} onClick={(e) => {
                  if (mode === "select") {
                    e.stopPropagation();
                    onSelectZone(zone.id);
                  }
                }}>
                  <polygon 
                    points={ptsStr} 
                    fill="none" 
                    stroke="var(--border-color)" 
                    strokeWidth={isSelected ? 4 : 2}
                    className="zone-polygon"
                    style={{ cursor: "pointer" }}
                  />
                </g>
              );
            }

            const zoneColor = zone.color || "var(--primary)";
            
            // Calculate center for text label (simple bounding box center)
            const minX = Math.min(...zone.points.map(p => p.x));
            const maxX = Math.max(...zone.points.map(p => p.x));
            const minY = Math.min(...zone.points.map(p => p.y));
            const maxY = Math.max(...zone.points.map(p => p.y));
            const centerX = minX + (maxX - minX) / 2;
            const centerY = minY + (maxY - minY) / 2;

            return (
              <g key={zone.id} className={`zone-group ${isSelected ? "selected" : ""}`} onClick={(e) => {
                if (mode === "select") {
                  e.stopPropagation();
                  onSelectZone(zone.id);
                }
              }}>
                <polygon 
                  points={ptsStr} 
                  fill={zoneColor} 
                  fillOpacity={isSelected ? 0.4 : 0.1} 
                  stroke={zoneColor} 
                  strokeWidth={isSelected ? 3 : 2}
                  className="zone-polygon"
                  style={{ cursor: "pointer" }}
                />
                <text 
                  x={centerX} 
                  y={centerY} 
                  textAnchor="middle" 
                  alignmentBaseline="middle"
                  fill="var(--text-primary)"
                  fontSize="14px"
                  fontWeight="600"
                  pointerEvents="none"
                >
                  {zone.name}
                </text>
              </g>
            );
          })}

          {/* Render Active Drawing */}
          {(mode === "draw" || mode === "draw-outline") && drawingPoints.length > 0 && (
            <g className="active-drawing">
              <polygon 
                points={previewPointsStr} 
                fill={mode === "draw-outline" ? "none" : "var(--accent)"} 
                fillOpacity={mode === "draw-outline" ? "0" : "0.2"} 
                stroke={mode === "draw-outline" ? "var(--border-color)" : "var(--accent)"} 
                strokeWidth="2" 
                strokeDasharray="4 4"
                pointerEvents="none"
              />
              {drawingPoints.map((p, i) => (
                <circle key={i} cx={p.x} cy={p.y} r="4" fill="#ffffff" pointerEvents="none" />
              ))}
              <circle cx={mousePos.x} cy={mousePos.y} r="5" fill="var(--accent)" pointerEvents="none" />
            </g>
          )}
        </svg>
      </div>
    </div>
  );
}
